import type { Asset } from '@compass/client/types/asset';
import type { CallInfo } from '@compass/client/types/call';
import { useMemo } from 'react';

import { useCall } from '@/hooks/useCalls';

const EMPTY_ASSETS: Asset[] = [];

/**
 * Hook to fetch full call details and extract audio assets for playback.
 *
 * Shared between AudioPlayerDialog (Calls page) and CallHistoryItem
 * (Create Case sidebar) to avoid duplicating the useCall → assets extraction logic.
 *
 * @param call - The CallInfo object to fetch details for (or null)
 * @param shouldFetch - Whether to actually fetch (e.g., only when expanded/open)
 */
// eslint-disable-next-line complexity
export function useCallAudioAssets(call: CallInfo | null, shouldFetch: boolean) {
  const caseRecordId = call?.caserecord_id ?? null;
  const callId = call?.id ?? null;
  const enabled = shouldFetch && (call?.assets?.length ?? 0) > 0;

  const { data: callDetails } = useCall(enabled ? caseRecordId : null, enabled ? callId : null);

  const fullCall = callDetails?.call ?? null;
  const recordingAssetId = fullCall?.assets?.[0]?.recording;

  const audioAssets = useMemo(
    (): Asset[] => (recordingAssetId ? [{ id: recordingAssetId } as Asset] : EMPTY_ASSETS),
    [recordingAssetId],
  );

  return { audioAssets, fullCall, recordingAssetId };
}
